/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — CWFIS fire stage family (operator-cop-doctrine
 *  §3A.7, §8.4 decision a, 2026-06-03).
 *
 *  This module is the SINGLE source of the CWFIS stage-of-control palette. It is
 *  consumed ONLY by the fire family: the Reported Wildfires / Fire Risk map
 *  layer (ActiveFiresLayer) and the fire detail panels (SidePanel FireStageBadge).
 *
 *  Palette (locked law — do NOT re-tune the hex values here):
 *    OC-FUL flame #e25822 (Colour Law 2 (c) — the burnt-orange flame red, kept off
 *    the saturated-red band the urgent anomaly owns) · OC-Monitored orange #f97316
 *    · OC-Unknown ember #b45309 (a NEUTRAL-HOT token — an OC fire whose response
 *    posture the source did NOT report; hot-but-unclassified, deliberately NOT the
 *    calmer managed orange, so "unknown" is never laundered into "Monitored")
 *    · BH yellow #eab308 · UC blue #3b82f6 · OUT/unknown grey #6b7280
 *
 *  Overlap debt this lock resolves (§3A.7): hues that mean something else in
 *  OTHER families must never leak across the boundary —
 *    - blue (#3b82f6 UC fire) also means L2 response and freezing-rain CAP
 *    - orange/amber (OC-Monitored #f97316) is distinct from L3-response amber
 *      and severe-storm CAP amber
 *
 *  ⛔ Do NOT import from ./responseLevel or ./capHazards (enforced by the
 *     no-restricted-imports lint rule in eslint.config.js). Color is never
 *     load-bearing alone: EVERY wildfire stage renders the CAHS `fire` glyph
 *     (a neutral hazard-TYPE symbol), and stage-of-control is carried by colour
 *     + glyph opacity (resolved UC/OUT render muted) + size + the panel stage
 *     badge (FireStageBadge) — never by the symbol SHAPE (§A evolved 2026-06-23,
 *     Phase 5c: see the FireRenderStyle block below).
 * ──────────────────────────────────────────────────────────────────────────
 */
import { FIRE_STAGE_LABELS, isFireStage, type FireStage } from '@tsr/shared-types';

/**
 * Stage-of-control visual language for the Fire Risk layer (E.2.1). One colour
 * per canonical stage, mirroring the CWFIS palette; an unknown / unmapped stage
 * falls back to grey (and the layer logs a client-side WARN). Shared by the map
 * markers/polygons, the side-panel badge, and the layer-toggle swatch so the
 * three stay in lockstep.
 */
export interface FireStageStyle {
  /** Canonical stage key (uppercased) or 'unknown'. */
  key: string;
  /** CSS hex, used for marker, polygon fill (with alpha) + stroke. */
  color: string;
  /** Polygon fill opacity. */
  fillAlpha: number;
  /** Polygon stroke width in px. */
  strokeWidth: number;
  /** Display label for the badge. */
  label: string;
}

const STAGE_STYLES: Record<FireStage, FireStageStyle> = {
  // L.2.1 full colour system: OC red, BH yellow (was amber — now distinct from
  // the OC-Monitored orange below), UC blue, OUT grey.
  OC: { key: 'OC', color: '#e25822', fillAlpha: 0.25, strokeWidth: 2, label: FIRE_STAGE_LABELS.OC },
  BH: { key: 'BH', color: '#eab308', fillAlpha: 0.2, strokeWidth: 2, label: FIRE_STAGE_LABELS.BH },
  UC: { key: 'UC', color: '#3b82f6', fillAlpha: 0.15, strokeWidth: 1, label: FIRE_STAGE_LABELS.UC },
  OUT: {
    key: 'OUT',
    color: '#6b7280',
    fillAlpha: 0.1,
    strokeWidth: 1,
    label: FIRE_STAGE_LABELS.OUT,
  },
};

const UNKNOWN_STYLE: FireStageStyle = {
  key: 'unknown',
  color: '#6b7280',
  fillAlpha: 0.1,
  strokeWidth: 1,
  label: 'Unknown stage',
};

/**
 * Resolve the render style for a stage-of-control value. Tolerant of casing,
 * null, and unmapped codes (which resolve to the grey 'unknown' style); callers
 * that need to flag the unmapped case should check `isFireStage` themselves.
 */
export function fireStageStyle(stage: string | null | undefined): FireStageStyle {
  const norm = typeof stage === 'string' ? stage.trim().toUpperCase() : '';
  return isFireStage(norm) ? STAGE_STYLES[norm] : UNKNOWN_STYLE;
}

/* ------------------------------------------------------------------------- *
 *  L.2.1 + Phase 5c — full (stage, response_type) marker symbology + hectare sizing.
 *
 *  §A EVOLVED (founder ruling, 2026-06-23): the prior "the red FLAME is reserved for
 *  OC-FUL; every other stage is a bare coloured dot" rule is SUPERSEDED. EVERY
 *  wildfire stage now renders the SAME base symbol — the CAHS `fire` glyph (a
 *  billboard, tinted to the stage colour). Stage-of-control is no longer carried by
 *  the symbol SHAPE; it is carried by:
 *    • COLOUR   — the LOCKED stage palette (do NOT re-tune):
 *                 OC-FUL #e25822 · OC-Monitored #f97316 · OC-Unknown #b45309 · BH #eab308 · UC #3b82f6 · OUT #6b7280
 *    • OPACITY  — live stages (OC-FUL / OC-Monitored / BH) at FULL ink; resolved
 *                 stages (UC / OUT) MUTED (~0.55) so a resolved fire recedes into
 *                 the ambient register (it's situational-awareness data — a UC fire
 *                 is a committed-resource site, an OUT fire is recent history — not
 *                 an act-now signal — but is never DROPPED).
 *    • SIZE     — the CWFIS hectare band (small/medium/large).
 *    • the panel STAGE BADGE (FireStageBadge).
 *
 *  Over-signalling is avoided NOT by hiding stages behind shape, but because the
 *  CAHS mark is a neutral hazard-TYPE symbol (not an emotive inferno) and resolved
 *  stages render muted. The additive-ring channel is RESERVED for the shipped 5b
 *  owned/managed ring (shape = fire · ring = managed · colour = stage — three
 *  orthogonal channels); stage NEVER adds a ring.
 *
 *    OC + FUL      → fire glyph, full ink (#e25822)   "OC — Full Response"
 *    OC + MOD      → fire glyph, full ink (#f97316)   "OC — Modified Response"  (crews engaged)
 *    OC + MON      → fire glyph, full ink (#f97316)   "OC — Monitored Response" (unfought)
 *    OC + response not reported (null / non-CIFFC) → fire glyph, full ink (#b45309)  "OC — response not reported"
 *                     (honest UNKNOWN: hot-but-unclassified — the source gave no CIFFC class,
 *                      and unknown ≠ the benign "Monitored". MOD + MON share the orange hue —
 *                      no 4th OC hex — but carry DISTINCT labels; Modified ≠ Monitored.)
 *    BH         → fire glyph, full ink    (#eab308)
 *    UC         → fire glyph, MUTED       (#3b82f6)
 *    OUT        → fire glyph, MUTED       (#6b7280)
 *    unknown    → fire glyph, full ink    (#6b7280)   (a data gap — kept visible)
 * ------------------------------------------------------------------------- */

/** OC-FUL ink (Colour Law 2 (c) burnt-orange #e25822) — the OC-FUL `fire`-glyph
 *  tint + the OC polygon fill/stroke. Distinct from the urgent anomaly red
 *  (#ff3b30) and the outage reds; shape no longer carries the family identity (all
 *  stages share the fire glyph), so colour + the SHAPE-vs-pulse contrast against
 *  the anomaly dot does. */
export const OC_FUL_COLOR = '#e25822';
/** OC-Monitored colour (orange) — OC fires the source classed MON/MOD (monitored /
 *  modified response). Distinct from the OC-Unknown ember below: this is a REPORTED
 *  posture, not a data gap. */
export const OC_MONITORED_COLOR = '#f97316';
/** OC-Unknown colour (ember #b45309) — an OC fire whose response posture the SOURCE
 *  did NOT report (response_type null; all 4 provincial feeds omit the CWFIS response
 *  class). A NEUTRAL-HOT token: hot-but-unclassified, deliberately NOT the calmer
 *  managed orange, so an UNKNOWN response is never laundered into the benign "Monitored"
 *  (unknown ≠ zero). Full ink (OC is a live stage) — muted only in hue, never in urgency. */
export const OC_UNKNOWN_COLOR = '#b45309';

/** The FOUR honest OC render/badge labels (do NOT collapse — CIFFC glossary
 *  distinguishes all three response classes, and the UNKNOWN label must never equal
 *  a reported one; that is the #1 honesty gate). CIFFC-VERBATIM class names (fetched
 *  https://glossary.ciffc.ca/Home/DownloadGlossary/EN, 2026-07-11): "Full Response
 *  (FUL)" / "Modified Response (MOD)" / "Monitored Response (MON)". Modified ≠
 *  Monitored: MOD crews conduct "direct and indirect attack … within a pre-determined
 *  perimeter" — labelling a MOD fire "Monitored" is a FALSE statement of operational
 *  fact. "response not reported" is an honesty qualifier (a data-provenance fact), NOT
 *  an Ontario IMS doctrine term — the doctrine stage stays "Out of Control"
 *  (FIRE_STAGE_LABELS.OC), carried by the FireStageBadge; these qualify the RESPONSE
 *  posture on top of that stage. */
export const OC_FUL_LABEL = 'OC — Full Response';
export const OC_MODIFIED_LABEL = 'OC — Modified Response';
export const OC_MONITORED_LABEL = 'OC — Monitored Response';
export const OC_UNKNOWN_LABEL = 'OC — response not reported';

/** The OC response-posture class — the honest FOUR-way split of the CWFIS/CIFFC
 *  response_type (supersedes the retired MON‖MOD-collapsing isMonitoredResponse):
 *    FUL     → Full Response (crews engaged, suppressing) — the authoritative OC-FUL
 *    MOD     → Modified Response (crews engaged: direct + indirect attack within a
 *              pre-determined perimeter) — DISTINCT from MON, never "Monitored"
 *    MON     → Monitored Response (observed, NOT suppressed) — the unfought posture
 *    UNKNOWN → the source reported no class (null / Ontario MNP·MDP·-1 / anything else)
 *              — the honest "response not reported", NEVER laundered into MON.
 *  Tolerant of casing / whitespace. */
export type OcPostureClass = 'FUL' | 'MOD' | 'MON' | 'UNKNOWN';
export function ocPostureClass(responseType: string | null | undefined): OcPostureClass {
  switch (typeof responseType === 'string' ? responseType.trim().toUpperCase() : '') {
    case 'FUL':
      return 'FUL';
    case 'MOD':
      return 'MOD';
    case 'MON':
      return 'MON';
    default:
      return 'UNKNOWN';
  }
}

/** True when an OC fire is under a FULL response (the authoritative OC-FUL fire —
 *  drives the co-located-hotspot suppression + the flame ink). Tolerant of casing /
 *  whitespace. */
export function isFullResponse(responseType: string | null | undefined): boolean {
  return ocPostureClass(responseType) === 'FUL';
}

/** Plain-language, ATTRIBUTED gloss of a raw UPSTREAM stage code the province filed (#618
 *  Fix 1b provenance) — so the operator can understand WHY a fire reads "Not reported by
 *  source" (e.g. Ontario filed 'BM' = a fire it is OBSERVING, which is not a CIFFC response
 *  class). Only the known non-CIFFC posture-in-stage-slot code is glossed; every other raw
 *  value returns null (the provenance line is then omitted — the derived stage already says
 *  it). Deliberately NOT "Monitored" (that is the CIFFC MON posture the honesty rule reserves)
 *  and never the bare raw response code ('MNP'/'-1' stays out of the operator's flow, carried
 *  only in the hover title). Attributed to the province, so it is provenance, not a doctrine label. */
export function upstreamStageGloss(raw: string | null | undefined): string | null {
  switch (typeof raw === 'string' ? raw.trim().toUpperCase() : '') {
    case 'BM':
      return 'Being Observed';
    default:
      return null;
  }
}

/** Human label for the CIFFC response_type code on the fire detail panel's "Response"
 *  line (L.2.1) — CIFFC-VERBATIM class names (glossary 2026-07-11). A NULL / empty /
 *  UNMAPPED response_type is the honest "Not reported by source" (the source omitted or
 *  used a non-CIFFC class), NEVER a bare "—" that reads as N/A, NEVER "Monitored"
 *  (unknown ≠ the benign managed posture), and NEVER the raw code rendered literally
 *  (a "-1" / "MNP" must never reach an operator). Moved here (was SidePanel-local) so
 *  the honesty wording is pinned from the light styles module. */
export function responseTypeLabel(rt: string | null | undefined): string {
  switch (ocPostureClass(rt)) {
    case 'FUL':
      return 'Full Response';
    case 'MOD':
      return 'Modified Response';
    case 'MON':
      return 'Monitored Response';
    default:
      return 'Not reported by source';
  }
}

/** Glyph billboard opacity (§A evolved, Phase 5c). Live stages render at FULL ink;
 *  resolved stages (UC under-control, OUT extinguished) render MUTED so they recede
 *  into the ambient register without being dropped. A deuteranope reads live-vs-
 *  resolved by this BRIGHTNESS step, not by hue alone (colourLaw.deuteranopia.test). */
export const GLYPH_OPACITY_LIVE = 1.0;
export const GLYPH_OPACITY_RESOLVED = 0.55;

/** The resolved stages that render muted. OC-FUL / OC-Monitored / BH (and an
 *  unmapped stage — a data gap we keep visible) render at full ink. */
function isResolvedStage(key: string): boolean {
  return key === 'UC' || key === 'OUT';
}

export interface FireRenderStyle {
  /** Every wildfire stage renders the CAHS `fire` glyph — the base symbol is FIXED;
   *  stage is carried by `color` + `glyphOpacity` + size + the panel badge, never by
   *  shape (§A evolved). The literal is a forward-looking seam should the family ever
   *  carry a second hazard glyph. */
  glyph: 'fire';
  /** Glyph tint; also the perimeter polygon fill (with alpha) + stroke. */
  color: string;
  /** Glyph billboard opacity — full for live stages, muted for resolved (UC/OUT). */
  glyphOpacity: number;
  /** Perimeter polygon fill opacity (kept on the render style so a fire's polygon
   *  stays in lockstep with its glyph tint — the OC-Monitored polygon is orange,
   *  not the OC base red). */
  fillAlpha: number;
  /** Perimeter polygon stroke width in px. */
  strokeWidth: number;
  /** Display label (badge / debug). */
  label: string;
}

/**
 * Resolve the Phase 5c render style for a fire from its stage + response_type.
 * Builds on `fireStageStyle` (so polygon alpha/stroke stay in lockstep) and layers
 * the OC-FUL / OC-Monitored-orange tint distinction + the live/resolved glyph
 * opacity on top. ALL stages return `glyph: 'fire'` — the symbol shape never
 * encodes stage (§A evolved 2026-06-23).
 */
export function fireRenderStyle(
  stage: string | null | undefined,
  responseType: string | null | undefined,
): FireRenderStyle {
  const base = fireStageStyle(stage);
  const glyphOpacity = isResolvedStage(base.key) ? GLYPH_OPACITY_RESOLVED : GLYPH_OPACITY_LIVE;
  if (base.key === 'OC') {
    // HONEST FOUR-WAY split of the OC response posture (do NOT collapse — CIFFC
    // distinguishes MOD from MON, and UNKNOWN must never read as "Monitored"):
    //   FUL → Full Response, flame ink (#e25822)
    //   MOD → Modified Response, orange (#f97316) — crews engaged; DISTINCT label from MON
    //   MON → Monitored Response, orange (#f97316) — the unfought posture
    //   UNKNOWN (null / MNP·MDP·-1 / anything non-CIFFC) → the honest OC-Unknown
    //           ("response not reported"), a distinct neutral-HOT ember (#b45309), never
    //           the calmer managed orange.
    // INK: MOD + MON share the orange hue (no 4th OC hex — the colour-law / deuteranopia
    // locks + a council ruling own that decision; the DISTINCTION carries in the LABEL).
    // OC is a live stage → full ink for all four.
    const oc = ((): { color: string; label: string } => {
      switch (ocPostureClass(responseType)) {
        case 'FUL':
          return { color: OC_FUL_COLOR, label: OC_FUL_LABEL };
        case 'MOD':
          return { color: OC_MONITORED_COLOR, label: OC_MODIFIED_LABEL };
        case 'MON':
          return { color: OC_MONITORED_COLOR, label: OC_MONITORED_LABEL };
        default:
          return { color: OC_UNKNOWN_COLOR, label: OC_UNKNOWN_LABEL };
      }
    })();
    return {
      glyph: 'fire',
      color: oc.color,
      glyphOpacity,
      fillAlpha: base.fillAlpha,
      strokeWidth: base.strokeWidth,
      label: oc.label,
    };
  }
  return {
    glyph: 'fire',
    color: base.color,
    glyphOpacity,
    fillAlpha: base.fillAlpha,
    strokeWidth: base.strokeWidth,
    label: base.label,
  };
}

/**
 * True for an officially out-of-control fire under a FULL response (stage OC AND
 * response_type FUL) — the authoritative OC-FUL fire. This is the SAME set that used
 * to render the reserved flame; the glyph swap changed how ALL fires LOOK, but this
 * predicate is UNCHANGED. It drives (a) the co-located-hotspot suppression — only an
 * OC-FUL fire suppresses a stacked AI hotspot dot (§G precision guard; a UC/OUT or
 * OC-Monitored fire never suppresses) — and (b) the OC-FUL ink tint above.
 */
export function isOcFul(
  stage: string | null | undefined,
  responseType: string | null | undefined,
): boolean {
  return fireStageStyle(stage).key === 'OC' && isFullResponse(responseType);
}

/** Hectare band per the CWFIS size legend: 0–100 small, 100–1000 medium, >1000
 *  large. null / non-positive hectares fall to 'small'. */
export type FireSizeBand = 'small' | 'medium' | 'large';
export function fireSizeBand(hectares: number | null | undefined): FireSizeBand {
  if (typeof hectares === 'number' && hectares > 1000) return 'large';
  if (typeof hectares === 'number' && hectares > 100) return 'medium';
  return 'small';
}

/** CAHS `fire`-glyph billboard pixel size per hectare band (Phase 5c: every stage
 *  now renders the glyph billboard at this size — the name is kept from the flame
 *  era per the size-constant lock). */
export const FLAME_SIZE_PX: Record<FireSizeBand, number> = { small: 20, medium: 28, large: 36 };
/** Dot pixel size per hectare band — the canvas-free fallback point (when the glyph
 *  can't rasterise) + the legacy hit-target floor. Static — per the founder motion-
 *  budget revision (2026-06-05, §3B.8) stage markers never pulse. The smallest band
 *  is held at >=12px so every fire is a comfortable click target (#358 — small fires
 *  were unclickable at 8px); Cesium points pick within their rendered pixelSize, so
 *  the visual size IS the hit target. */
export const DOT_SIZE_PX: Record<FireSizeBand, number> = { small: 12, medium: 16, large: 20 };

/* ------------------------------------------------------------------------- *
 *  Track B (#441) — reported-fire ESCALATION ranking, render channel.
 *
 *  A reported fire's escalation index (0–100; the forward-looking OC-risk estimate
 *  from fusion/reported-fire-escalation.ts) makes the few fires about to RUN read
 *  distinctly on the map so an operator can point scarce drone/aircraft monitoring
 *  at them. This is expressed as a STATIC SIZE BOOST ONLY — it stays inside every
 *  established lock:
 *    • COLOUR: NO new hue. The locked stage palette is untouched; escalation never
 *      retints a fire (an escalating BH fire is still BH-yellow, just larger).
 *    • MOTION: NO pulse / animation (founder motion-budget law, §3B.8) — a constant
 *      pixel multiplier, never an animated property.
 *    • SHAPE / RING: unchanged (the fire glyph + the 5b owned ring are orthogonal).
 *  Bands mirror the scorer (extreme ≥75, high ≥50). null / unscored = 1.0 (no change).
 * ------------------------------------------------------------------------- */
export const ESCALATION_SIZE_BOOST_EXTREME = 1.5; // index ≥ 75
export const ESCALATION_SIZE_BOOST_HIGH = 1.25; // index ≥ 50
export function escalationSizeBoost(index: number | null | undefined): number {
  if (typeof index !== 'number') return 1;
  if (index >= 75) return ESCALATION_SIZE_BOOST_EXTREME;
  if (index >= 50) return ESCALATION_SIZE_BOOST_HIGH;
  return 1;
}
